import { isFunction, isNil } from "lodash";
import {
    Chip as PrimeReactChip,
    ChipProps as PrimeReactChipProps,
} from "primereact/chip";
import { Tooltip } from "primereact/tooltip";
import { forwardRef, MouseEvent, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import {
    IconCheck,
    IconClose,
} from "../../../features/layout/components/Icons";
import { generateId, mergePt } from "../../utils";

export const CHIP_TYPES = Object.freeze({
    PRIMARY: "PRIMARY",
    SECONDARY: "SECONDARY",
    TERTIARY: "TERTIARY",
    NEUTRAL: "NEUTRAL",
    SUCCESS: "SUCCESS",
    WARNING: "WARNING",
    ERROR: "ERROR",
    INFO: "INFO",
} as const);

export type ChipType = (typeof CHIP_TYPES)[keyof typeof CHIP_TYPES];

export const CHIP_SIZE = Object.freeze({
    xs: "xs",
    sm: "sm",
    md: "md",
    lg: "lg",
} as const);

export type ChipSize = (typeof CHIP_SIZE)[keyof typeof CHIP_SIZE];

const SIZE_CLASSNAMES = Object.freeze({
    xs: "h-5 px-2 text-xxs leading-[0.6875rem]",
    sm: "h-6 px-2.5 text-xs leading-3",
    md: "h-7 px-3 text-xs leading-3",
    lg: "h-8 px-4 text-base leading-[0.875rem]",
} as const);

const TYPE_CLASSNAMES = Object.freeze({
    [CHIP_TYPES.PRIMARY]: {
        outlined: { static: "", hover: "hover:bg-primary-90" },
        solid: {
            static: "bg-primary-500 border-primary-500 text-primary-text",
            hover: "hover:bg-primary-550",
        },
    },
    [CHIP_TYPES.NEUTRAL]: {
        outlined: {
            static: "bg-transparent border-surface-600 text-text-500",
            hover: "hover:bg-surface-510",
        },
        solid: {
            static: "bg-surface-500 border-surface-500 text-text-500",
            hover: "hover:bg-surface-550",
        },
    },
    [CHIP_TYPES.SECONDARY]: {
        outlined: {
            static: "bg-secondary-50 border-secondary-200 text-secondary-750",
            hover: "hover:bg-secondary-100",
        },
        solid: {
            static: "bg-secondary-500 border-secondary-500 text-secondary-text",
            hover: "hover:bg-secondary-550",
        },
    },
    [CHIP_TYPES.TERTIARY]: {
        outlined: {
            static: "bg-tertiary-50 border-tertiary-50 text-tertiary-700",
            hover: "hover:bg-tertiary-100",
        },
        solid: {
            static: "bg-tertiary-500 border-tertiary-500 text-tertiary-text",
            hover: "hover:bg-tertiary-550",
        },
    },
    [CHIP_TYPES.INFO]: {
        outlined: {
            static: "bg-info-50 border-info-200 text-info-750",
            hover: "hover:bg-info-100",
        },
        solid: {
            static: "bg-info-500 border-info-500 text-info-text",
            hover: "hover:bg-info-550",
        },
    },
    [CHIP_TYPES.ERROR]: {
        outlined: {
            static: "bg-error-50 border-error-200 text-error-750",
            hover: "hover:bg-error-100",
        },
        solid: {
            static: "bg-error-500 border-error-500 text-error-text",
            hover: "hover:bg-error-550",
        },
    },
    [CHIP_TYPES.WARNING]: {
        outlined: {
            static: "bg-warning-50 border-warning-200 text-warning-750",
            hover: "hover:bg-warning-100",
        },
        solid: {
            static: "bg-warning-500 border-warning-500 text-warning-text",
            hover: "hover:bg-warning-550",
        },
    },
    [CHIP_TYPES.SUCCESS]: {
        outlined: {
            static: "bg-success-50 border-success-200 text-success-750",
            hover: "hover:bg-success-100",
        },
        solid: {
            static: "bg-success-500 border-success-500 text-success-text",
            hover: "hover:bg-success-550",
        },
    },
} as const);

export interface ChipProps
    extends Omit<
        PrimeReactChipProps,
        "type" | "onRemove" | "label" | "removable"
    > {
    outlined?: boolean;
    disabled?: boolean;
    type?: ChipType;
    label?: ReactNode;
    tooltip?: ReactNode;
    isActive?: boolean;
    onRemove?: (event: MouseEvent) => void;
    size?: ChipSize;
}

const Chip = forwardRef<PrimeReactChip, ChipProps>(function Chip(
    props: ChipProps,
    ref
) {
    const {
        disabled,
        children,
        className = null,
        onRemove,
        label,
        tooltip,
        isActive,
        size = CHIP_SIZE.md,
        type = CHIP_TYPES.PRIMARY,
        outlined = true,
        ...restProps
    } = props;

    const { pt: ptProp, onClick: onClickProp, ...rest } = restProps;

    const tooltipTargetClass = generateId("chip-tooltip");
    const hasTooltip = !isNil(tooltip);

    const isRemovable = isFunction(onRemove);
    const isInteractive = !disabled && (isRemovable || isFunction(onClickProp));

    const typeClassNames =
        TYPE_CLASSNAMES[type][outlined ? "outlined" : "solid"];

    const handleRemove = (event: MouseEvent) => {
        if (disabled || !onRemove) {
            return;
        }
        event.stopPropagation();
        onRemove(event);
    };

    return (
        <>
            {hasTooltip && (
                <Tooltip
                    target={`.${tooltipTargetClass}`}
                    position="top"
                    mouseTrack
                    mouseTrackTop={15}
                    className="max-w-sm"
                    pt={{ text: { className: "wrap-break-word" } }}
                >
                    {tooltip}
                </Tooltip>
            )}
            <PrimeReactChip
                ref={ref}
                onMouseDown={
                    isRemovable ? (event) => event.preventDefault() : undefined
                }
                onClick={isRemovable ? handleRemove : onClickProp}
                className={twMerge(
                    "inline-flex w-fit items-center",
                    disabled && "opacity-60",
                    SIZE_CLASSNAMES[size],
                    typeClassNames.static,
                    isInteractive &&
                        `cursor-pointer transition-colors duration-150 ease-[ease] delay-200 hover:delay-0 ${typeClassNames.hover}`,
                    hasTooltip && tooltipTargetClass,
                    className
                )}
                // @ts-ignore-next-line (because label should be string but we are using jsx-element here. it works)
                label={
                    isRemovable || isActive ? (
                        <div className="flex items-center whitespace-nowrap min-w-0">
                            {isActive && (
                                <span className="mr-1.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                                    <IconCheck />
                                </span>
                            )}
                            {label}
                            {isRemovable && (
                                /* Overwrite PrimeReact onRemove behaviour */
                                <span
                                    className="-my-1.5 -mr-1.5 flex shrink-0 cursor-pointer items-center justify-center p-1.5 opacity-60 hover:opacity-100"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleRemove(event);
                                    }}
                                >
                                    <IconClose />
                                </span>
                            )}
                        </div>
                    ) : (
                        label
                    )
                }
                pt={mergePt(
                    { label: { className: "my-0 whitespace-nowrap" } },
                    ptProp
                )}
                {...rest}
            >
                {children}
            </PrimeReactChip>
        </>
    );
});

export default Chip;
